import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { useStore } from "../../../../stores";
import { FallbackImage } from "../../../../components";
import { Message } from "../../../../models";

const ContextualReplyPreview = () => {
  const { contextualReplyToMessage, setContextualReplyToMessage } =
    useStore("chats");

  if (!contextualReplyToMessage) return null;

  return (
    <Preview
      message={contextualReplyToMessage}
      onClear={() => setContextualReplyToMessage()}
    />
  );
};

const Preview = ({
  message,
  onClear,
}: {
  message: Message;
  onClear: () => void;
}) => {
  const { selectedBot } = useStore("bots");
  const { selectedChat } = useStore("chats");
  const { users } = useStore("users");

  const {
    text,
    attachment,
    attachments,
    isFromUser,
    senderId,
    firstName,
    lastName,
    isDeleted,
  } = message;

  // Get user info (similar to Message component logic)
  const user = useMemo(() => {
    if (isFromUser) {
      return {
        name: firstName
          ? `${firstName} ${lastName || ""}`.trim()
          : `${selectedChat?.firstName || ""} ${
              selectedChat?.lastName || ""
            }`.trim(),
        image: selectedChat?.image,
      };
    }
    return users?.find((f) => f.id === senderId);
  }, [
    firstName,
    isFromUser,
    lastName,
    selectedChat?.firstName,
    selectedChat?.image,
    selectedChat?.lastName,
    senderId,
    users,
  ]);

  // Get display name
  const displayName = useMemo(() => {
    if (isFromUser) {
      return user?.name || "You";
    }
    return user?.name || selectedBot?.name || "Bot";
  }, [isFromUser, user?.name, selectedBot?.name]);

  // Get preview content
  const getPreviewContent = () => {
    if (isDeleted) {
      return "This message was deleted";
    }

    // Handle attachments
    if (attachment) {
      switch (attachment.type) {
        case "image":
          return "📷 Photo";
        case "video":
          return "🎥 Video";
        case "audio":
          return "🎵 Audio";
        case "file":
          return "📄 Document";
        case "location":
          return "📍 Location";
        case "vcard":
          return "👤 Contact";
        case "story_mention":
          return "📖 Story";
        case "voice_call":
          return "📞 Voice call";
        case "video_call":
          return "📹 Video call";
        default:
          return text || "Media";
      }
    }

    if (attachments && attachments.length > 0) {
      if (attachments.length === 1) {
        const type = attachments[0].type;
        switch (type) {
          case "image":
            return "📷 Photo";
          case "video":
            return "🎥 Video";
          case "audio":
            return "🎵 Audio";
          case "file":
            return "📄 Document";
          default:
            return text || "Media";
        }
      } else {
        return `📎 ${attachments.length} attachments`;
      }
    }

    return text || "Message";
  };

  const previewContent = getPreviewContent();
  const truncatedContent =
    previewContent.length > 100
      ? previewContent.substring(0, 100) + "..."
      : previewContent;

  let fallbackUrl = "/assets/avatar.svg";
  if (!isFromUser && !user) {
    fallbackUrl = "/assets/bot.svg";
  }

  return (
    <div className="is-flex is-flex-direction-column is-align-items-flex-start">
      <div
        className="is-flex is-align-items-center w-100 px-3 py-2"
        style={{
          backgroundColor: "#f0f2f5",
          borderLeft: "4px solid #2a79ef",
          borderRadius: "8px",
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          className="delete is-small"
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            backgroundColor: "rgba(0,0,0,0.1)",
          }}
          onClick={onClear}
          aria-label="Cancel reply"
        />

        {/* User avatar */}
        <figure className="is-flex-shrink-0 mr-2">
          <p className="image is-24x24">
            <FallbackImage
              className="is-rounded"
              src={(user as any)?.avatarUrl}
              fallback={fallbackUrl}
              alt={displayName}
            />
          </p>
        </figure>

        {/* Content */}
        <div className="is-flex-grow-1" style={{ minWidth: 0 }}>
          <div className="is-flex is-align-items-center mb-1">
            <span
              className="has-text-weight-semibold is-size-7"
              style={{ color: isFromUser ? "#2a79ef" : "#667781" }}
            >
              {displayName}
            </span>
          </div>
          <div
            className="is-size-7"
            style={{
              color: "#667781",
              wordBreak: "break-word",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {truncatedContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(ContextualReplyPreview);
